import { useEffect, useMemo, useState } from 'react';
import { SCUIAutoCompleteInput, SCUIButton, SCUIDateRangerV2, SCUITextField } from '../../../atomic/atoms';
import { SCUIQuickDateSelector } from '../../../atomic/molecules';
import {
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Rating,
    Select,
    Box,
    InputLabel,
    IconButton,
    FormControl,
    Chip,
    Tooltip,
    Typography,
    Autocomplete,
    TextField,
    Checkbox,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import TagRoundedIcon from '@mui/icons-material/TagRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import { useTranslation } from 'react-i18next';
import { CommentQuery, SellerPortalProductQuery } from '../../../services/query';
import { useLookupLists } from './helper';
import { CF } from './commentFilter.classes';

function CommentFilterV2({
    onClose,
    endDate,
    setEndDate,
    createdDate,
    setCreatedDate,
    selectedStatus,
    setSelectedStatus,
    selectedGrievance,
    setSelectedGrievance,
    tagNames,
    setParams,
    params,
    refetchGridData,
}: any) {
    const [selectedIsAgentValue, setSelectedIsAgentValue] = useState(params.isAgent);
    const [selectedStarValue, setSelectedStarValue] = useState(params.starScore);
    const [operators, setOperators] = useState<string[]>([]);
    const [selectedOperator, setSelectedOperator] = useState<any>(params.operator);
    const [selectedHasPhoto, setSelectedHasPhoto] = useState(
        params.hasPhoto !== undefined ? params.hasPhoto : 2 // 2: Tümü, 1: Var, 0: Yok
    );
    const [subBrands, setSubBrands] = useState<any[]>([]);
    const [selectedSubBrand, setSelectedSubBrand] = useState<string>(params.subBrandName || '');
    const [subBrandSearchText, setSubBrandSearchText] = useState('');
    const [selectedIsShipped, setSelectedIsShipped] = useState<boolean>(params.isShipped || false);

    const { t } = useTranslation();
    const { agentStatusList, commentStatusLookupList } = useLookupLists();
    const formPanelSx = {
        p: { xs: 2, md: 2.5 },
        borderRadius: '24px',
        border: '1px solid rgba(148, 163, 184, 0.18)',
        background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.96) 100%)',
        boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
    } as const;

    const fieldLabelRowSx = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: 28,
        minWidth: 28,
        height: 44,
        color: '#475569',
    } as const;

    const fieldItemSx = {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        width: '100%',
    } as const;

    const fieldContentSx = {
        flex: 1,
        minWidth: 0,
    } as const;

    const sharedFormControlSx = {
        width: '100%',
        minWidth: 0,
        '& .MuiInputBase-root': {
            borderRadius: '14px',
            backgroundColor: '#fff',
        },
        '& .MuiSelect-select': {
            alignItems: 'center',
            minHeight: '20px',
            display: 'flex',
        },
    } as const;

    const radioGroupCardSx = {
        padding: '8px 16px',
        borderRadius: '18px',
        border: '1px solid rgba(148, 163, 184, 0.18)',
        backgroundColor: '#fff',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.85)',
    } as const;

    const onChangeIsAgentValue = (event: any) => {
        setSelectedIsAgentValue(event.target.value);
        setParams((prevParams: any) => ({
            ...prevParams,
            isAgent: event.target.value,
        }));
    };

    const _handleChangeDate = (event: any) => {
        setCreatedDate(event[0]);
        setEndDate(event[1]);
        setParams((prevParams: any) => ({
            ...prevParams,
            createDate: event[0],
            endDate: event[1],
        }));
    };

    const handleClose = () => {
        if (onClose) onClose();
    };

    const handleClick = () => {
        refetchGridData(0);
        onClose();
    };

    const handleQuickDateRangeSelect = (startDate: string, endDate: string) => {
        _handleChangeDate([startDate, endDate]);
    };

    const _handleChangeTag = (_: any, newValue: any) => {
        setParams((prevParams: any) => ({
            ...prevParams,
            tagName: newValue,
        }));
    };

    useEffect(() => {
        CommentQuery.CommentOperatorListV2().then((response) => {
            if (response) {
                setOperators(response);
            }
        });
    }, []);

    useEffect(() => {
        if (subBrandSearchText.length >= 3) {
            SellerPortalProductQuery.GetSearchBrands(`searchText=${subBrandSearchText}&brandStatus=true`).then(
                (response: any) => {
                    if (response) {
                        setSubBrands(Array.isArray(response) ? response : response.data || []);
                    }
                }
            );
        } else if (subBrandSearchText.length === 0) {
            setSubBrands([]);
        }
    }, [subBrandSearchText]);

    const optionsWithSelected = useMemo(() => {
        if (!selectedSubBrand) return subBrands;
        const exists = subBrands.some((b) => b.value === selectedSubBrand);
        if (exists) return subBrands;
        return [{ id: -1, value: selectedSubBrand }, ...subBrands];
    }, [subBrands, selectedSubBrand]);

    return (
        <Box
            className={CF.root}
            sx={{
                p: { xs: 1.5, md: 2 },
                '& .MuiFormLabel-root': { transform: 'translate(14px, -15px) scale(1)', color: '#0f172a' },
                '& .MuiFormControlLabel-label': { fontSize: '0.98rem', color: '#334155' },
                '& .MuiRadio-root': {
                    p: 0.75,
                },
            }}
        >
            <Box
                className={CF.header}
                sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    mb: 3,
                    p: { xs: 2, md: 2.5 },
                    borderRadius: '24px',
                    border: '1px solid rgba(148, 163, 184, 0.16)',
                    background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(239,246,255,0.95) 100%)',
                    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
                }}
            >
                <Box
                    className={CF.headerTitleGroup}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}
                >
                    <Box
                        className={CF.headerIconWrap}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 44,
                            height: 44,
                            borderRadius: '14px',
                            backgroundColor: 'rgba(37, 99, 235, 0.1)',
                            color: '#2563eb',
                        }}
                    >
                        <FilterAltRoundedIcon />
                    </Box>
                    <Box>
                        <Typography
                            className={CF.headerTitle}
                            sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.55rem' }, color: '#0f172a' }}
                        >
                            {t('commentFilter.header')}
                        </Typography>
                        <Chip
                            className={CF.headerChip}
                            label="Filtreler"
                            size="small"
                            sx={{
                                mt: 0.75,
                                fontWeight: 700,
                                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                                color: '#1d4ed8',
                            }}
                        />
                    </Box>
                </Box>
                <IconButton
                    className={CF.closeButton}
                    sx={{
                        marginLeft: 'auto',
                        border: '1px solid rgba(148, 163, 184, 0.22)',
                        backgroundColor: '#fff',
                        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
                        transition: 'all 0.2s ease',
                        '& .MuiSvgIcon-root': {
                            width: '1.7rem',
                            height: '1.7rem',
                            color: '#0f172a',
                        },
                        '&:hover': {
                            transform: 'rotate(90deg)',
                            backgroundColor: '#f8fafc',
                        },
                    }}
                    onClick={handleClose}
                >
                    <CloseRoundedIcon />
                </IconButton>
            </Box>
            <Box
                className={CF.panel}
                sx={{
                    ...formPanelSx,
                }}
            >
                <Box
                    className={CF.fieldsGrid}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            md: 'repeat(2, minmax(0, 1fr))',
                            lg: 'repeat(3, minmax(0, 1fr))',
                        },
                        columnGap: { xs: 2, md: 3.5, lg: 4 },
                        rowGap: 4,
                        alignItems: 'center',
                    }}
                >
                    <Box sx={fieldItemSx}>
                        <Box sx={fieldLabelRowSx}>
                            <BadgeRoundedIcon sx={{ color: '#2563eb', fontSize: 26 }} />
                        </Box>
                        <Box sx={fieldContentSx}>
                            <FormControl sx={sharedFormControlSx} fullWidth>
                                <InputLabel shrink>{t('commentFilter.commentId')}</InputLabel>
                                <div className={CF.fieldCommentId}>
                                    <SCUITextField
                                        size={'l'}
                                        fullWidth
                                        variant={'outlined'}
                                        value={params.id}
                                        stateName={'id'}
                                        onChange={(event: any) =>
                                            setParams((prevParams: any) => ({
                                                ...prevParams,
                                                id: event.trim(),
                                            }))
                                        }
                                    />
                                </div>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={fieldItemSx}>
                        <Box sx={fieldLabelRowSx}>
                            <CalendarMonthRoundedIcon sx={{ color: '#0f766e', fontSize: 26 }} />
                        </Box>
                        <Box sx={fieldContentSx}>
                            <FormControl sx={sharedFormControlSx} fullWidth>
                                <InputLabel shrink>{t('commentFilter.dateRange')}</InputLabel>
                                <div className={CF.fieldDateRangeWrap}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SCUIDateRangerV2
                                            fromDate={createdDate}
                                            toDate={endDate}
                                            setDateRange={_handleChangeDate}
                                            dateFormat="YYYY-MM-DD"
                                            containerStyling={{ width: '100%' }}
                                        />
                                        <SCUIQuickDateSelector
                                            onDateRangeSelect={handleQuickDateRangeSelect}
                                            translationNamespace="comments"
                                        />
                                    </Box>
                                </div>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={fieldItemSx}>
                        <Box sx={fieldLabelRowSx}>
                            <StarRoundedIcon sx={{ color: '#f59e0b', fontSize: 26 }} />
                        </Box>
                        <Box sx={fieldContentSx}>
                            <FormControl sx={sharedFormControlSx} fullWidth>
                                <InputLabel shrink>{t('commentFilter.starRating')}</InputLabel>
                                <Box
                                    className={CF.fieldStarRating}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1.25,
                                        flexWrap: 'nowrap',
                                        minHeight: 44,
                                    }}
                                >
                                    <Rating
                                        name="simple-controlled"
                                        value={selectedStarValue}
                                        size="large"
                                        sx={{
                                            '& .MuiRating-icon': {
                                                '& .MuiSvgIcon-root': {
                                                    width: '28px',
                                                    height: '28px',
                                                },
                                            },
                                        }}
                                        onChange={(event: any, newValue: any) => {
                                            setSelectedStarValue(newValue);
                                            setParams((prevParams: any) => ({
                                                ...prevParams,
                                                starScore: event.target.value,
                                            }));
                                        }}
                                    />
                                    <Chip
                                        className={CF.fieldStarResetChip}
                                        icon={<RestartAltRoundedIcon />}
                                        label="Sifirla"
                                        clickable
                                        onClick={() => {
                                            setSelectedStarValue(0);
                                            setParams((prevParams: any) => ({
                                                ...prevParams,
                                                starScore: 0,
                                            }));
                                        }}
                                        sx={{
                                            height: 34,
                                            borderRadius: '999px',
                                            fontWeight: 700,
                                            backgroundColor: 'rgba(245, 158, 11, 0.12)',
                                            color: '#b45309',
                                            border: '1px solid rgba(245, 158, 11, 0.2)',
                                            ml: 'auto',
                                            flexShrink: 0,
                                            '&:hover': {
                                                backgroundColor: 'rgba(245, 158, 11, 0.18)',
                                            },
                                        }}
                                    />
                                </Box>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={fieldItemSx}>
                        <Box sx={fieldLabelRowSx}>
                            <BadgeRoundedIcon sx={{ color: '#2563eb', fontSize: 26 }} />
                        </Box>
                        <Box sx={fieldContentSx}>
                            <FormControl sx={sharedFormControlSx} fullWidth>
                                <InputLabel shrink>{t('commentFilter.customerId')}</InputLabel>
                                <div className={CF.fieldCustomerId}>
                                    <SCUITextField
                                        size={'m'}
                                        variant={'outlined'}
                                        fullWidth
                                        value={params.CustomerId}
                                        stateName={'CustomerId'}
                                        onChange={(event: any) =>
                                            setParams((prevParams: any) => ({
                                                ...prevParams,
                                                CustomerId: event.trim(),
                                            }))
                                        }
                                    />
                                </div>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={fieldItemSx}>
                        <Box sx={fieldLabelRowSx}>
                            <TaskAltRoundedIcon sx={{ color: '#16a34a', fontSize: 26 }} />
                        </Box>
                        <Box sx={fieldContentSx}>
                            <FormControl sx={sharedFormControlSx} fullWidth>
                                <InputLabel id="selectStatus-label" shrink>
                                    {t('comments.selectStatus')}
                                </InputLabel>
                                <Select
                                    className={CF.fieldStatusSelect}
                                    sx={{ height: 44 }}
                                    labelId="selectStatus-label"
                                    id="parentMenuLabel"
                                    value={selectedStatus}
                                    onChange={(event: any) => {
                                        setSelectedStatus(event.target.value);
                                        setParams((prevParams: any) => ({
                                            ...prevParams,
                                            commentStatus: [{ id: event.target.value }],
                                        }));
                                    }}
                                    fullWidth
                                >
                                    {commentStatusLookupList.map((data: any) => (
                                        <MenuItem
                                            className={CF.fieldStatusMenuItem}
                                            key={data.id}
                                            id={data.id}
                                            value={data.id}
                                        >
                                            {data.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={fieldItemSx}>
                        <Box sx={fieldLabelRowSx}>
                            <ReportProblemOutlinedIcon sx={{ color: '#ea580c', fontSize: 26 }} />
                        </Box>
                        <Box sx={fieldContentSx}>
                            <FormControl sx={sharedFormControlSx} fullWidth>
                                <InputLabel shrink>{t('commentFilter.grievance')}</InputLabel>
                                <Box sx={radioGroupCardSx}>
                                    <RadioGroup
                                        className={CF.fieldGrievanceGroup}
                                        row
                                        value={selectedGrievance}
                                        onChange={(event: any) => {
                                            setSelectedGrievance(event.target.value);
                                            setParams((prevParams: any) => ({
                                                ...prevParams,
                                                grievanceStatus: [{ id: event.target.value, value: event.target.name }],
                                            }));
                                        }}
                                    >
                                        <FormControlLabel
                                            className={CF.fieldGrievanceRadioAll}
                                            value={2}
                                            control={<Radio />}
                                            label={t('general.all')}
                                        />
                                        <FormControlLabel
                                            className={CF.fieldGrievanceRadioExists}
                                            value={1}
                                            control={<Radio />}
                                            label={t('general.exists')}
                                        />
                                        <FormControlLabel
                                            className={CF.fieldGrievanceRadioNone}
                                            value={0}
                                            control={<Radio />}
                                            label={t('general.none')}
                                        />
                                    </RadioGroup>
                                </Box>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={fieldItemSx}>
                        <Box sx={fieldLabelRowSx}>
                            <LabelRoundedIcon sx={{ color: '#7c3aed', fontSize: 26 }} />
                        </Box>
                        <Box sx={fieldContentSx}>
                            <FormControl sx={sharedFormControlSx} fullWidth>
                                <InputLabel shrink>{t('commentFilter.specialCode')}</InputLabel>
                                <div className={CF.fieldSpecialCode}>
                                    <SCUITextField
                                        size={'m'}
                                        fullWidth
                                        variant={'outlined'}
                                        value={params.specialCode}
                                        stateName={'specialCode'}
                                        onChange={(event: any) => {
                                            const newSpecialCode = event.trim();
                                            setParams((prevParams: any) => ({
                                                ...prevParams,
                                                specialCode: newSpecialCode,
                                            }));
                                        }}
                                    />
                                </div>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={fieldItemSx}>
                        <Box sx={fieldLabelRowSx}>
                            <TagRoundedIcon sx={{ color: '#2563eb', fontSize: 26 }} />
                        </Box>
                        <Box sx={fieldContentSx}>
                            <FormControl sx={sharedFormControlSx} fullWidth>
                                <Tooltip title={params.tagName?.length > 1 ? `${params.tagName.length} secim` : ''}>
                                    <Box className={CF.fieldTagsWrap} sx={{ width: '100%' }}>
                                        {tagNames && (
                                            <SCUIAutoCompleteInput
                                                defValue={params.tagName}
                                                list={tagNames}
                                                onChange={_handleChangeTag}
                                                limitTags={1}
                                                txtPlaceHolder={t('general.tag')}
                                                searchValue="keyWord"
                                            />
                                        )}
                                    </Box>
                                </Tooltip>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={fieldItemSx}>
                        <Box sx={fieldLabelRowSx}>
                            <SupportAgentRoundedIcon sx={{ color: '#0f766e', fontSize: 26 }} />
                        </Box>
                        <Box sx={fieldContentSx}>
                            <FormControl sx={sharedFormControlSx} fullWidth>
                                <InputLabel shrink>{t('commentFilter.isAgent')}</InputLabel>
                                <Box sx={radioGroupCardSx}>
                                    <RadioGroup
                                        className={CF.fieldIsAgentGroup}
                                        aria-label="isAgent"
                                        name="isAgent"
                                        value={selectedIsAgentValue}
                                        onChange={onChangeIsAgentValue}
                                        row
                                    >
                                        {agentStatusList.map((status) => (
                                            <FormControlLabel
                                                key={status.value}
                                                value={status.id}
                                                control={<Radio />}
                                                label={status.value}
                                            />
                                        ))}
                                    </RadioGroup>
                                </Box>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={fieldItemSx}>
                        <Box sx={fieldLabelRowSx}>
                            <PaletteOutlinedIcon sx={{ color: '#7c3aed', fontSize: 26 }} />
                        </Box>
                        <Box sx={fieldContentSx}>
                            <FormControl sx={sharedFormControlSx} fullWidth>
                                <InputLabel shrink>{t('commentFilter.colorCode')}</InputLabel>
                                <div className={CF.fieldColorCode}>
                                    <SCUITextField
                                        size={'m'}
                                        variant={'outlined'}
                                        fullWidth
                                        value={params.colorCode}
                                        stateName={'colorCode'}
                                        onChange={(event: any) => {
                                            const newColorCode = event.trim();
                                            setParams((prevParams: any) => ({
                                                ...prevParams,
                                                colorCode: newColorCode,
                                            }));
                                        }}
                                    />
                                </div>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={fieldItemSx}>
                        <Box sx={fieldLabelRowSx}>
                            <PersonSearchRoundedIcon sx={{ color: '#ea580c', fontSize: 26 }} />
                        </Box>
                        <Box sx={fieldContentSx}>
                            <FormControl sx={sharedFormControlSx} fullWidth>
                                <InputLabel shrink>{t('commentFilter.operatingUser')}</InputLabel>
                                <Select
                                    className={CF.fieldOperatorSelect}
                                    sx={{ height: 44 }}
                                    value={selectedOperator}
                                    onChange={(event: any) => {
                                        setSelectedOperator(event.target.value);
                                        setParams((prevParams: any) => ({
                                            ...prevParams,
                                            operator: event.target.value,
                                        }));
                                    }}
                                >
                                    {operators.map((operator: any) => (
                                        <MenuItem
                                            className={CF.fieldOperatorMenuItem}
                                            key={operator.username}
                                            value={operator.userRef}
                                        >
                                            {operator.username}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={fieldItemSx}>
                        <Box sx={fieldLabelRowSx}>
                            <PhotoLibraryRoundedIcon sx={{ color: '#2563eb', fontSize: 26 }} />
                        </Box>
                        <Box sx={fieldContentSx}>
                            <FormControl sx={sharedFormControlSx} fullWidth>
                                <InputLabel shrink>{t('commentFilter.photoComment')}</InputLabel>
                                <Box sx={radioGroupCardSx}>
                                    <RadioGroup
                                        className={CF.fieldHasPhotoGroup}
                                        aria-label="hasPhoto"
                                        name="hasPhoto"
                                        value={selectedHasPhoto}
                                        onChange={(event: any) => {
                                            setSelectedHasPhoto(Number(event.target.value));
                                            setParams((prevParams: any) => ({
                                                ...prevParams,
                                                hasPhoto: Number(event.target.value),
                                            }));
                                        }}
                                        row
                                    >
                                        <FormControlLabel
                                            className={CF.fieldHasPhotoRadioAll}
                                            value={2}
                                            control={<Radio />}
                                            label={t('general.all')}
                                        />
                                        <FormControlLabel
                                            className={CF.fieldHasPhotoRadioExists}
                                            value={1}
                                            control={<Radio />}
                                            label={t('general.exists')}
                                        />
                                        <FormControlLabel
                                            className={CF.fieldHasPhotoRadioNone}
                                            value={0}
                                            control={<Radio />}
                                            label={t('general.none')}
                                        />
                                    </RadioGroup>
                                </Box>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={fieldItemSx}>
                        <Box sx={fieldLabelRowSx}>
                            <StorefrontRoundedIcon sx={{ color: '#059669', fontSize: 26 }} />
                        </Box>
                        <Box sx={fieldContentSx}>
                            <FormControl sx={sharedFormControlSx} fullWidth>
                                <InputLabel shrink>{t('commentFilter.subBrand')}</InputLabel>
                                <Autocomplete
                                    options={optionsWithSelected}
                                    getOptionLabel={(option: any) => option.value || ''}
                                    value={optionsWithSelected.find((b) => b.value === selectedSubBrand) || null}
                                    onInputChange={(_, newInputValue) => {
                                        setSubBrandSearchText(newInputValue);
                                    }}
                                    onChange={(_, newValue: any) => {
                                        const val = newValue ? newValue.value : '';
                                        setSelectedSubBrand(val);
                                        setParams((prevParams: any) => ({
                                            ...prevParams,
                                            subBrandName: val,
                                        }));
                                    }}
                                    isOptionEqualToValue={(option, value) => {
                                        if (!option || !value) return false;
                                        return option.value === value.value;
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={t('commentFilter.subBrandSearchPlaceholder')}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    height: 44,
                                                    borderRadius: '14px',
                                                    backgroundColor: '#fff',
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={fieldItemSx}>
                        <Box sx={fieldLabelRowSx}>
                            <LocalShippingRoundedIcon sx={{ color: '#d97706', fontSize: 26 }} />
                        </Box>
                        <Box sx={fieldContentSx}>
                            <FormControl sx={sharedFormControlSx} fullWidth>
                                <Box
                                    sx={{
                                        ...radioGroupCardSx,
                                        display: 'flex',
                                        alignItems: 'center',
                                        height: 44,
                                        padding: '0 16px',
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedIsShipped}
                                                onChange={(e) => {
                                                    setSelectedIsShipped(e.target.checked);
                                                    setParams((prevParams: any) => ({
                                                        ...prevParams,
                                                        isShipped: e.target.checked,
                                                    }));
                                                }}
                                            />
                                        }
                                        label={t('commentFilter.cancelledOrder')}
                                    />
                                </Box>
                            </FormControl>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box
                className={CF.actionsRow}
                sx={{
                    mt: 4,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <SCUIButton
                    className={CF.applyButton}
                    type={'primary'}
                    sizeType={'fixed'}
                    size={'m'}
                    style={{ width: '320px' }}
                    text={t('general.filter')}
                    onClick={handleClick}
                />
            </Box>
        </Box>
    );
}

export default CommentFilterV2;
